import { makeid } from 'utils/helpers/makeid';

import {
  booleanOptions,
  notEscalatedOptions,
  nursingInterventionOptions,
  stringBooleanOptions,
  commonFactors,
} from './common';

import type { TFormItem, IFormItemOption } from '../types';

export const factorsAssessment: IFormItemOption<string>[] = [
  ...commonFactors,
  {
    label: 'Starting a new medication or supplement',
    value: 'newMedicationOrSupplement',
    key: makeid(10),
  },
  {
    label: 'Psychosocial factors - stress, boredom, anxiety or depression',
    value: 'psychosocialFactors',
    key: makeid(10),
  },
  {
    label: 'Changes in lifestyle behaviors - nicotine/alcohol/substance use',
    value: 'changesInLifestyleBehaviors',
    key: makeid(10),
  },
  {
    label: 'Someone else used my scale',
    value: 'someoneElseUsedScale',
    key: makeid(10),
  },
];

const weightAssessmentOptions: IFormItemOption[] = [
  {
    label: 'What do you eat/drink on a typical day? How much?',
    value: 'whatDoYouEatDrinkOnTypicalDay',
    key: makeid(10),
  },
  {
    label: 'What kind of physical activity do you enjoy?',
    value: 'whatKindOfPhysicalActivity',
    key: makeid(10),
  },
  {
    label: 'What is the best time of day for you to be active?',
    value: 'whatIsBestTimeOfDay',
    key: makeid(10),
  },
  {
    label: 'How much time do you spend sitting on a typical day?',
    value: 'howMuchTimeSpendSitting',
    key: makeid(10),
  },
];

export const assessmentWeightForm: TFormItem[] = [
  {
    key: makeid(10),
    title: '#1 Did you speak to patient?',
    description:
      'Choose other if unlisted family member & type in their relationship. (Ex: Daughter in law) Add Name of Individual you spoke to in other.',
    type: 'radio',
    required: true,
    name: 'patient',
    options: [
      { value: 'Patient', label: 'Patient', key: makeid(10) },
      { value: 'Spouse', label: 'Spouse', key: makeid(10) },
      { value: 'Caregiver', label: 'Caregiver', key: makeid(10) },
      {
        value: 'Emergency Contacts',
        label: 'Emergency Contacts',
        key: makeid(10),
      },
      { value: null, label: 'Other', key: makeid(10) },
    ],
    extra: [
      {
        type: 'doubleRowInput',
        name: 'other',
        key: makeid(10),
        options: [
          { label: 'Other Member', value: 'other', key: makeid(10) },
          { label: 'Relationship', value: 'relationship', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#2 Mental Status Assessment',
    description:
      'Oriented and Coherent 1: Person 2: Person, place 3: Person, place, time 4: Person, place, time, situation?',
    type: 'radio',
    required: false,
    name: 'AOC',
    options: [
      { value: 'x1', label: 'x1', key: makeid(10) },
      { value: 'x2', label: 'x2', key: makeid(10) },
      { value: 'x3', label: 'x3', key: makeid(10) },
      { value: 'x4', label: 'x4', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#3 Appointments',
    type: 'datepicker',
    name: 'datepicker',
    required: false,
    options: [
      {
        value: 'last',
        label: "Last Doctor's Appointment",
        type: 'max',
        key: makeid(10),
      },
      {
        value: 'next',
        label: "Next Doctor's Appointment",
        type: 'min',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#4 Patient is able to take another reading at time of call?',
    subtitle: 'Is the reading still out of bounds?',
    type: 'radio',
    required: true,
    name: 'isOneMoreReading',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'radio',
        name: 'bounds',
        label: 'Is the reading still out of bounds?',
        key: makeid(10),
        options: booleanOptions,
        divider: true,
      },
      {
        type: 'input',
        name: 'repeatReadingWas',
        key: makeid(10),
        options: [
          {
            label: 'The repeat reading was:',
            value: 'repeatReadingWas',
            key: makeid(10),
          },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#5 Is it okay if we discuss your weight today?',
    type: 'radio',
    required: true,
    name: 'isAgreeWeightDiscuss',
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#6 Was this weight change intentional?',
    type: 'radio',
    required: false,
    name: 'isWeightChangeIntentional',
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#7 Does patient take any weight loss medications or supplements?',
    type: 'radio',
    required: false,
    name: 'isTakeWeightLossMedications',
    options: booleanOptions,
    extra: [
      {
        type: 'text',
        name: 'weightLossMedicationsOther',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#8 Factors Assessment',
    type: 'radio-list',
    required: false,
    options: factorsAssessment,
    name: 'factorsAssessment',
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#9 Weight Assessment',
    type: 'list',
    required: false,
    options: weightAssessmentOptions,
    name: 'weightAssessment',
    extra: [
      {
        type: 'text',
        name: 'weightAssessmentOther',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#10 Nursing Intervention',
    type: 'checkbox',
    name: 'nursingIntervention',
    required: true,
    options: nursingInterventionOptions,
    extra: [
      {
        type: 'labelCheckbox',
        name: 'notEscalated',
        key: makeid(10),
        label: 'Not Escalated',
        options: notEscalatedOptions,
        divider: true,
      },
      {
        name: 'interventionDetails',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#11 Patient Understanding',
    description:
      'Patient verbalized understanding and is agreeable to nursing recommendation?',
    type: 'radio',
    required: false,
    name: 'verbalized',
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
];
